// Minimal blue line icon set — stroke-based, 24x24 viewBox.
// Ported from the ImageShield reference design (src/icons.jsx).
// All icons take className and accept extra props.
import React from "react";

const Icon = ({
  d,
  className = "w-5 h-5",
  stroke = "currentColor",
  fill = "none",
  strokeWidth = 1.75,
  children,
  ...rest
}) => (
  <svg
    viewBox="0 0 24 24"
    fill={fill}
    stroke={stroke}
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    {...rest}
  >
    {d ? <path d={d} /> : children}
  </svg>
);

export const Icons = {
  Shield: (p) => <Icon {...p} d="M12 3l8 3v6c0 4.5-3.2 8.3-8 9-4.8-.7-8-4.5-8-9V6l8-3z" />,
  QR: (p) => (
    <Icon {...p}>
      <rect x="3" y="3" width="7" height="7" rx="1" />
      <rect x="14" y="3" width="7" height="7" rx="1" />
      <rect x="3" y="14" width="7" height="7" rx="1" />
      <path d="M14 14h3v3h-3zM20 14v3M14 20h3M20 20h1" />
    </Icon>
  ),
  Camera: (p) => (
    <Icon {...p}>
      <path d="M4 8h3l2-2h6l2 2h3a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9a1 1 0 0 1 1-1z" />
      <circle cx="12" cy="13" r="3.5" />
    </Icon>
  ),
  Cpu: (p) => (
    <Icon {...p}>
      <rect x="6" y="6" width="12" height="12" rx="2" />
      <rect x="9" y="9" width="6" height="6" rx="1" />
      <path d="M9 2v3M15 2v3M9 19v3M15 19v3M2 9h3M2 15h3M19 9h3M19 15h3" />
    </Icon>
  ),
  Message: (p) => <Icon {...p} d="M21 12a8 8 0 1 1-3.2-6.4L21 4l-1 4.5A8 8 0 0 1 21 12z" />,
  User: (p) => (
    <Icon {...p}>
      <circle cx="12" cy="8" r="4" />
      <path d="M4 21c1.5-4 4.5-6 8-6s6.5 2 8 6" />
    </Icon>
  ),
  Logout: (p) => (
    <Icon {...p}>
      <path d="M15 4h3a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2h-3" />
      <path d="M10 17l-5-5 5-5M5 12h11" />
    </Icon>
  ),
  Plus: (p) => <Icon {...p} d="M12 5v14M5 12h14" />,
  X: (p) => <Icon {...p} d="M6 6l12 12M18 6L6 18" />,
  Calendar: (p) => (
    <Icon {...p}>
      <rect x="3" y="5" width="18" height="16" rx="2" />
      <path d="M3 9h18M8 3v4M16 3v4" />
    </Icon>
  ),
  Clock: (p) => (
    <Icon {...p}>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </Icon>
  ),
  Pin: (p) => (
    <Icon {...p}>
      <path d="M12 22s7-7.5 7-12a7 7 0 1 0-14 0c0 4.5 7 12 7 12z" />
      <circle cx="12" cy="10" r="2.5" />
    </Icon>
  ),
  Trash: (p) => (
    <Icon {...p}>
      <path d="M4 7h16M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
      <path d="M6 7l1 13a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2l1-13" />
      <path d="M10 11v7M14 11v7" />
    </Icon>
  ),
  Edit: (p) => (
    <Icon {...p}>
      <path d="M14 4l6 6-11 11H3v-6L14 4z" />
      <path d="M13 5l6 6" />
    </Icon>
  ),
  Heart: ({ filled, className = "w-5 h-5", ...rest }) => (
    <Icon
      className={className}
      fill={filled ? "#ef4444" : "none"}
      stroke={filled ? "#ef4444" : "currentColor"}
      d="M12 21s-7-4.5-9.5-9A5.5 5.5 0 0 1 12 6a5.5 5.5 0 0 1 9.5 6c-2.5 4.5-9.5 9-9.5 9z"
      {...rest}
    />
  ),
  Download: (p) => (
    <Icon {...p}>
      <path d="M12 4v12M7 11l5 5 5-5" />
      <path d="M4 20h16" />
    </Icon>
  ),
  Share: (p) => (
    <Icon {...p}>
      <circle cx="6" cy="12" r="2.5" />
      <circle cx="18" cy="6" r="2.5" />
      <circle cx="18" cy="18" r="2.5" />
      <path d="M8 11l8-4M8 13l8 4" />
    </Icon>
  ),
  Copy: (p) => (
    <Icon {...p}>
      <rect x="9" y="9" width="11" height="11" rx="2" />
      <path d="M5 15V5a1 1 0 0 1 1-1h10" />
    </Icon>
  ),
  Image: (p) => (
    <Icon {...p}>
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <circle cx="9" cy="10" r="2" />
      <path d="M21 17l-5-5-9 8" />
    </Icon>
  ),
  Users: (p) => (
    <Icon {...p}>
      <circle cx="9" cy="9" r="3" />
      <path d="M3 19c1-3 3-4.5 6-4.5s5 1.5 6 4.5" />
      <path d="M15 4a3 3 0 0 1 0 6" />
      <path d="M17 19c.5-2.5 1.7-3.7 3.5-4" />
    </Icon>
  ),
  Album: (p) => (
    <Icon {...p}>
      <rect x="4" y="3" width="16" height="18" rx="2" />
      <path d="M9 3v9l3-2 3 2V3" />
    </Icon>
  ),
  Chevron: (p) => <Icon {...p} d="M9 6l6 6-6 6" />,
  ChevronLeft: (p) => <Icon {...p} d="M15 6l-6 6 6 6" />,
  Upload: (p) => (
    <Icon {...p}>
      <path d="M12 16V4M7 9l5-5 5 5" />
      <path d="M4 20h16" />
    </Icon>
  ),
  Check: (p) => <Icon {...p} d="M5 12l5 5L20 7" />,
  Search: (p) => (
    <Icon {...p}>
      <circle cx="11" cy="11" r="7" />
      <path d="M20 20l-3.5-3.5" />
    </Icon>
  ),
  Send: (p) => <Icon {...p} d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" />,
  Instagram: (p) => (
    <Icon {...p}>
      <rect x="3" y="3" width="18" height="18" rx="5" />
      <circle cx="12" cy="12" r="4" />
      <circle cx="17.5" cy="6.5" r="0.8" fill="currentColor" />
    </Icon>
  ),
  Youtube: (p) => (
    <Icon {...p}>
      <rect x="2" y="5" width="20" height="14" rx="3" />
      <path d="M10 9l5 3-5 3V9z" fill="currentColor" stroke="none" />
    </Icon>
  ),
  Facebook: (p) => <Icon {...p} d="M14 7h3V3h-3a4 4 0 0 0-4 4v3H7v4h3v8h4v-8h3l1-4h-4V7z" />,
  X_social: (p) => <Icon {...p} fill="currentColor" stroke="none" strokeWidth={0} d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 5.933H2.736l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />,
  LinkedIn: (p) => (
    <Icon {...p}>
      <rect x="3" y="3" width="18" height="18" rx="3" />
      <path d="M8 10v7M8 7v.5M12 17v-4a2 2 0 0 1 4 0v4M12 10v7" />
    </Icon>
  ),
  WhatsApp: (p) => (
    <Icon {...p}>
      <path d="M3 21l1.7-5A8 8 0 1 1 8 19.3L3 21z" />
      <path d="M9 8.5c.2 2.5 2 4.3 4.5 4.5.5 0 .8-.2 1-.6l.3-.7-1.8-.9-.6.7c-.8-.4-1.4-1-1.8-1.8l.7-.6-.9-1.8-.7.3c-.4.2-.6.6-.6 1.2z" fill="currentColor" stroke="none" />
    </Icon>
  ),
};

export default Icons;
